import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { Header } from '../components/shell/Header'
import { TaskBoard } from '../components/task/TaskBoard'
import { TaskList } from '../components/task/TaskList'
import { QuickAdd } from '../components/task/QuickAdd'
import { NewTaskModal } from '../components/task/NewTaskModal'
import type { TaskDetailTab } from '../components/task/TaskDetail'
import { TaskDetail } from '../components/task/TaskDetail'
import { listTasks, listAssignments, listEmployees, listClients, type Assignment } from '../lib/api'
import { useSession } from '../hooks/useAuth'
import { useDisciplines } from '../hooks/useDisciplines'
import { LayoutGrid, List, Upload, Archive, Search, X } from 'lucide-react'
import { TaskImport } from '../components/task/TaskImport'
import { roleCanManage, roleCanWrite, roleCanBrowseClients, roleCanSeeAll } from '@shared/enums'
import { inWindow, WINDOW_LABELS, type DueWindow } from '../components/task/taskViews'
import { useDebounced } from '../hooks/useDebounced'

export function Tasks() {
  const { data: session } = useSession()
  const role = (session?.user as { role?: string } | undefined)?.role ?? 'member'
  const canManage = roleCanManage(role)
  const canWrite = roleCanWrite(role)
  const canOpenClients = roleCanBrowseClients(role)
  const currentEmployeeId = (session?.user as { employeeId?: string } | undefined)?.employeeId ?? null
  const [view, setView] = useState<'board' | 'list'>('board')
  const [showQuickAdd, setShowQuickAdd] = useState(false)
  // The full form (LXP-45). Holds the title carried over from Quick Add.
  const [newTask, setNewTask] = useState<{ title: string } | null>(null)
  // How to open the card — a freshly created task lands on the tab where
  // anything that didn't save is, with a notice saying what.
  // Keyed by task so opening some other card never inherits the notice.
  const [cardOpts, setCardOpts] = useState<{ taskId: string; tab: TaskDetailTab; notice: string | null } | null>(null)
  const [showImport, setShowImport] = useState(false)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [filters, setFilters] = useState<Record<string, string>>({})
  const [dueWindow, setDueWindow] = useState<DueWindow>('all')
  const [showArchived, setShowArchived] = useState(false)
  const [search, setSearch] = useState('')
  // Search runs server-side (title or client name), so it trails the input by a
  // beat rather than firing a request per keystroke.
  const debouncedSearch = useDebounced(search.trim())

  // `/tasks?task=<id>` — the deep link alert emails point at, so an overdue
  // notice lands on the card itself rather than on a list to search through.
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkedTaskId = searchParams.get('task')
  useEffect(() => {
    if (!deepLinkedTaskId) return
    setSelectedTaskId(deepLinkedTaskId)
    // The panel fetches the task by id, so the link still resolves for a task
    // that no filter or window would have shown. Drop the param once consumed
    // so a later close-and-reopen doesn't reopen it.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('task')
      return next
    }, { replace: true })
  }, [deepLinkedTaskId, setSearchParams])

  const effectiveFilters = {
    ...filters,
    ...(showArchived ? { archived: 'true' } : {}),
    // Omit the key entirely when empty — `search=''` would be a filter that
    // matches nothing meaningful and would churn the query cache.
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  }
  const { data, isLoading } = useQuery({
    queryKey: ['tasks', effectiveFilters],
    queryFn: () => listTasks(effectiveFilters),
  })

  // Assignment data for the whole task set (raw rows → grouped by task).
  const { data: assignmentsData } = useQuery({
    queryKey: ['assignments', { entityType: 'task' }],
    queryFn: () => listAssignments({ entityType: 'task' }),
  })
  const { data: employeesData } = useQuery({ queryKey: ['employees'], queryFn: () => listEmployees() })
  const { data: clientsData } = useQuery({ queryKey: ['clients', {}], queryFn: () => listClients() })

  const today = new Date().toISOString().slice(0, 10)
  const allTasks = data?.items ?? []
  const tasks = allTasks.filter((t) => inWindow(t.due, t.status, dueWindow, today))
  const employees = employeesData?.items ?? []
  const clients = clientsData?.items ?? []

  // Counts come from the unfiltered set so the tabs read as a standing summary
  // ("3 overdue") rather than changing meaning depending on which one is on.
  const windowCounts: Record<DueWindow, number> = {
    all: allTasks.length,
    overdue: allTasks.filter((t) => inWindow(t.due, t.status, 'overdue', today)).length,
    today: allTasks.filter((t) => inWindow(t.due, t.status, 'today', today)).length,
    week: allTasks.filter((t) => inWindow(t.due, t.status, 'week', today)).length,
  }

  const assignmentsByTask = new Map<string, Assignment[]>()
  for (const a of assignmentsData?.items ?? []) {
    const list = assignmentsByTask.get(a.entityId) ?? []
    list.push(a)
    assignmentsByTask.set(a.entityId, list)
  }

  return (
    <div className="page">
      <Header
        title="Tasks"
        onQuickAdd={() => setShowQuickAdd(true)}
        onNewTask={canWrite ? () => setNewTask({ title: '' }) : undefined}
      />

      {/* View toggle + filters */}
      <div className="toolbar">
        <div className="view-toggle">
          <button
            className={`view-toggle-btn${view === 'board' ? ' view-toggle-btn--active' : ''}`}
            onClick={() => setView('board')}
            aria-label="Board view"
          >
            <LayoutGrid size={16} />
          </button>
          <button
            className={`view-toggle-btn${view === 'list' ? ' view-toggle-btn--active' : ''}`}
            onClick={() => setView('list')}
            aria-label="List view"
          >
            <List size={16} />
          </button>
        </div>

        {/* Search — server-side, over task title and client name. */}
        <div className="search-input">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search tasks or clients…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search tasks"
          />
          {search && (
            <button
              type="button"
              className="search-input__clear"
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Due-window tabs — the first thing reached for on a Monday. */}
        <div className="window-tabs">
          {(Object.keys(WINDOW_LABELS) as DueWindow[]).map((w) => (
            <button
              key={w}
              className={`window-tab${dueWindow === w ? ' window-tab--active' : ''}${
                w === 'overdue' && windowCounts.overdue > 0 ? ' window-tab--danger' : ''
              }`}
              onClick={() => setDueWindow(w)}
            >
              {WINDOW_LABELS[w]}
              <span className="window-tab__count mono">{windowCounts[w]}</span>
            </button>
          ))}
        </div>

        <TaskFilters
          filters={filters}
          onChange={setFilters}
          employees={employees}
          clients={clients}
          currentEmployeeId={(session?.user as { employeeId?: string } | undefined)?.employeeId}
        />

        <button
          className={`btn btn-ghost btn-sm${showArchived ? ' btn--active' : ''}`}
          style={{ marginLeft: 'auto' }}
          onClick={() => setShowArchived((v) => !v)}
          title={showArchived ? 'Back to active tasks' : 'View archived tasks'}
        >
          <Archive size={15} /> {showArchived ? 'Archived' : 'Archive'}
        </button>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => setShowImport(true)}
        >
          <Upload size={15} /> Import
        </button>
      </div>

      {/* Quick add */}
      {showQuickAdd && (
        <QuickAdd
          onClose={() => setShowQuickAdd(false)}
          employees={employees}
          clients={clients}
          canAssign={canWrite}
          onExpand={
            canWrite
              ? (title) => {
                  setShowQuickAdd(false)
                  setNewTask({ title })
                }
              : undefined
          }
        />
      )}

      {/* Full creation form — alongside Quick add, not instead of it */}
      {newTask && (
        <NewTaskModal
          onClose={() => setNewTask(null)}
          onCreated={(taskId, opts) => {
            setNewTask(null)
            setCardOpts({ taskId, ...opts })
            setSelectedTaskId(taskId)
          }}
          employees={employees}
          clients={clients}
          canAssign={canWrite}
          initialTitle={newTask.title}
          currentEmployeeId={currentEmployeeId}
          creatorIsLead={!roleCanSeeAll(role)}
        />
      )}

      {/* Bulk import */}
      {showImport && (
        <TaskImport onClose={() => setShowImport(false)} clients={clients} employees={employees} />
      )}

      {/* Task detail slide-over */}
      {selectedTaskId && (
        <TaskDetail
          key={selectedTaskId}
          taskId={selectedTaskId}
          initialTab={cardOpts?.taskId === selectedTaskId ? cardOpts.tab : 'details'}
          notice={cardOpts?.taskId === selectedTaskId ? cardOpts.notice : null}
          onClose={() => {
            setSelectedTaskId(null)
            setCardOpts(null)
          }}
        />
      )}

      {/* Content */}
      {isLoading ? (
        <p className="text-muted" style={{ padding: 'var(--space-2)' }}>Loading...</p>
      ) : view === 'board' ? (
        <TaskBoard
          tasks={tasks}
          assignmentsByTask={assignmentsByTask}
          employees={employees}
          canWrite={canWrite}
          canOpenClients={canOpenClients}
          onTaskClick={setSelectedTaskId}
        />
      ) : (
        <TaskList
          tasks={tasks}
          assignmentsByTask={assignmentsByTask}
          employees={employees}
          canWrite={canWrite}
          canAssign={canWrite}
          canOpenClients={canOpenClients}
          currentEmployeeId={currentEmployeeId}
          scopedToAssigned={!roleCanSeeAll(role)}
          onTaskClick={setSelectedTaskId}
          bulkSelectable={view === 'list'}
        />
      )}
    </div>
  )
}

function TaskFilters({
  filters,
  onChange,
  employees,
  clients,
  currentEmployeeId,
}: {
  filters: Record<string, string>
  onChange: (f: Record<string, string>) => void
  employees: { id: string; name: string; active: boolean }[]
  clients: { id: string; name: string }[]
  currentEmployeeId?: string
}) {
  const disciplines = useDisciplines()
  const set = (key: string, value: string) => {
    const next = { ...filters }
    if (value) next[key] = value
    else delete next[key]
    onChange(next)
  }

  return (
    <div className="filter-group">
      <select value={filters.priority ?? ''} onChange={(e) => set('priority', e.target.value)} aria-label="Filter by priority">
        <option value="">All priorities</option>
        <option value="urgent">Urgent</option>
        <option value="high">High</option>
        <option value="med">Medium</option>
        <option value="low">Low</option>
      </select>
      <select value={filters.status ?? ''} onChange={(e) => set('status', e.target.value)} aria-label="Filter by status">
        <option value="">All statuses</option>
        <option value="todo">To Do</option>
        <option value="in_progress">In Progress</option>
        <option value="review">Review</option>
        <option value="done">Done</option>
        <option value="blocked">Blocked</option>
      </select>
      <select value={filters.discipline ?? ''} onChange={(e) => set('discipline', e.target.value)} aria-label="Filter by discipline">
        <option value="">All disciplines</option>
        {disciplines.map((d) => (
          <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
        ))}
      </select>
      <select value={filters.assigneeId ?? ''} onChange={(e) => set('assigneeId', e.target.value)} aria-label="Filter by assignee">
        <option value="">All assignees</option>
        {currentEmployeeId && <option value={currentEmployeeId}>My tasks</option>}
        {employees.filter((e) => e.active).map((e) => (
          <option key={e.id} value={e.id}>{e.name}</option>
        ))}
      </select>
      {/* Lists only the clients the viewer can already see — for a member that's
          their assigned accounts, whose names are on their task cards anyway. The
          Clients *module* is admin-only; filtering your own work by client is not. */}
      <select value={filters.clientId ?? ''} onChange={(e) => set('clientId', e.target.value)} aria-label="Filter by client">
        <option value="">All clients</option>
        {clients.map((c) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
    </div>
  )
}
